const authenticate = require('./authenticate');
const clientGenerator = require('./generator');
const fromPath = require('./fromPath');
const { resolveClient } = require('./client');

const CONFIG_PROPERTY = '!config';
const CLIENT_PROPERTY = '!client';
const isBrowser = !!process.browser;
const defaultQueryBuilder = ({ groupId, assetId, version }) => `https://anypoint.mulesoft.com/exchange/api/v1/assets/${groupId}/${assetId}/${version}/api/json`;

module.exports = function clientCreator(ramlClientOptions = {}) {
  const RAMLClient = {};
  const contextWindow = ramlClientOptions.contextWindow || global;
  const { editor } = ramlClientOptions;
  let clientId = 0;

  /**
   * Responsible for loading RAML documents and return API clients.
   *
   * @param {String}   name
   * @param {String}   uri
   * @param {Object}   [clientConfig]
   * @param {Function} done
   */

  RAMLClient.createClient = function (...args) {
    let name;
    let source;
    let clientConfig;
    let groupId;
    let assetId;
    let version;
    let addToContext = false;

    if (args.length <= 3) {
      if (typeof args[0] === 'object') {
        [source, clientConfig] = args;
      } else if (typeof args[0] === 'string' && typeof args[1] === 'string') {
        [name, source, clientConfig] = args;
      } else {
        [source, clientConfig] = args;
      }

      if (name) {
        addToContext = true;
      }
    } else {
      if (!ramlClientOptions.ramlQueryBuilder) {
        ramlClientOptions.ramlQueryBuilder = defaultQueryBuilder;
      }

      if (args.slice(0, 4).every(arg => typeof arg === 'string')) {
        [name, groupId, assetId, version, clientConfig] = args;
        addToContext = true;
      } else if (args.slice(0, 3).every(arg => typeof arg === 'string')) {
        [groupId, assetId, version, clientConfig] = args;
      } else {
        return Promise.reject(new Error('Invalid arguments'));
      }
    }

    if (!name) {
      name = `client${clientId || ''}`;
      clientId += 1;
    }

    clientConfig = clientConfig || {};

    /**
     * Generate and attach the RAML client from RAML.
     *
     * @param  {Object} raml
     */
    const createClient = function (raml) {
      if (isBrowser) {
        const client = clientGenerator(name, raml, Object.assign({}, ramlClientOptions, clientConfig));

        if (addToContext) {
          contextWindow[name] = client;
        }

        return fromPath({}, name.split('.'), client);
      }

      if (typeof raml === 'object') {
        return clientGenerator(name, raml, Object.assign({}, ramlClientOptions, clientConfig));
      }

      return Promise.reject(new Error('Invalid RAML'));
    };

    const resolveClientFn = ramlClientOptions.ramlResolver || resolveClient;

    return resolveClientFn({
      groupId, assetId, version, url: source, options: ramlClientOptions
    }).then(createClient);
  };

  /**
   * Set a configuration value on a client.
   *
   * @param {Function} client
   * @param {String}   key
   * @param {*}        value
   */
  RAMLClient.set = function (client, key, value) {
    // If we don't have enough arguments for a key and value, assume we have
    // a fresh configuration object.
    if (arguments.length < 3) {
      return Object.assign(client[CONFIG_PROPERTY], key);
    }

    client[CONFIG_PROPERTY][key] = value;

    return value;
  };

  /**
   * Retrieve a value from the client config object.
   *
   * @param  {Function} client
   * @param  {String}   key
   * @return {*}
   */
  RAMLClient.get = function (client, key) {
    if (arguments.length < 2) {
      return client[CONFIG_PROPERTY];
    }

    return client[CONFIG_PROPERTY][key];
  };

  /**
   * Unset a key from the client configuration.
   *
   * @param  {Function} client
   * @param  {String}   key
   * @return {Boolean}
   */
  RAMLClient.unset = function (client, key) {
    if (arguments.length < 2) {
      Object.keys(client[CONFIG_PROPERTY]).forEach(($key) => {
        delete client[CONFIG_PROPERTY][$key];
      });

      return true;
    }

    return delete client[CONFIG_PROPERTY][key];
  };

  /**
   * Authenticate a RAML API client passing an optional method and accompanying
   * options object.
   *
   * @param {Function} client
   * @param {String}   method
   * @param {Object}   options
   * @param {Function} done
   */
  RAMLClient.authenticate = function (client, method, authOptions) {
    const clientOption = client[CLIENT_PROPERTY];
    const { securitySchemes } = clientOption;

    // Possibility to use two params
    if (method && typeof method === 'object' && !authOptions) {
      authOptions = method;
      method = null;
    }

    // Extend passed in options with global options (for `baseUriParameters`).
    authOptions = Object.assign({}, client[CONFIG_PROPERTY], ramlClientOptions, authOptions);

    return authenticate(securitySchemes, method, authOptions)
      .then(({ scheme, options, tokens }) => {
        // Alias onto the client object for future use.
        clientOption.authentication[scheme.type] = Object.assign({}, options, tokens);

        // Set private fields to asterisks of an arbitrary length.
        Object.keys(tokens).forEach((key) => {
          if (authenticate.SECRET_FIELDS[key]) {
            tokens[key] = new Array(Math.ceil(tokens[key].length / 5) * 5).join('*');
          }
        });

        if (isBrowser) {
          return tokens;
        }

        this.set(client, 'session', tokens);

        return client;
      });
  };

  if (editor && !editor.languages.typescript.javascriptDefaults.getExtraLibs()['api.d.ts']) {
    editor.languages.typescript.javascriptDefaults.addExtraLib(`
      declare class API {
        /**
         * Generate an API client from a RAML document.
         *
         * @param {string} groupId
         * @param {string} assetId
         * @param {string} version
         * @param {any} config
         */
        static createClient(groupId: string, assetId: string, version: string, config?: { sync: boolean, baseUri: string }): any;

        /**
         * Generate an API client from a RAML document and alias it on the window.
         *
         * @param {string} name
         * @param {string} groupId
         * @param {string} assetId
         * @param {string} version
         * @param {any} config
         */
        static createClient(name: string, groupId: string, assetId: string, version: string, config?: { sync: boolean, baseUri: string }): any;

        /**
         * Generate an API client from a RAML document and alias it on the window.
         *
         * @param {string} url
         * @param {any} config
         */
        static createClient(name: string, url: string, config?: { sync: boolean, baseUri: string }): any;

        /**
         * Set a configuration option of a RAML API client.
         *
         * @param {any} client
         * @param {string} key
         * @param {any} value
         */
        static set(client: any, key: string, value: any): any;

        /**
         * Get a configuration option from a RAML API client.
         *
         * @param {any} client
         * @param {string} key
         */
        static get(client: any, key: string): any;

        /**
         * Authenticate a RAML API client with credentials.
         *
         * @param {any} client
         * @param {string} method
         * @param {any} options
         */
        static authenticate(client: any, method: string, options?: any): any;
      }
    `.replace(/ {6}/g, ''), 'api.d.ts');

    editor.languages.typescript.javascriptDefaults.addExtraLib(`
      declare class APIResponse {
        body: any;
        status: number;
        headers: any;
      }
    `.replace(/ {6}/g, ''), 'api-response.d.ts');
  }

  return RAMLClient;
};
